/**
 * Passport · Section F — Your future neighbours
 */
import React from 'react';
import { Users, Shield, Building2, TrendingDown, TrendingUp } from 'lucide-react';
import { Badge } from '../ui/badge';

const makeFmt = (section = {}) => {
  const locale = section.locale || 'en-IE';
  const currency = section.currency || 'EUR';
  return (n) =>
    n == null ? '—'
      : new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
};

const Pct = ({ pct, total = 100, color = 'bg-purple-500' }) => (
  <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
    <div className={`h-1.5 ${color}`} style={{ width: `${Math.min(100, Math.max(0, (pct / total) * 100))}%` }} />
  </div>
);

const FutureNeighboursSection = ({ section }) => {
  const fmtEur = makeFmt(section);
  const isUK = section.country_label === 'UK';
  const locale = section.locale || 'en-IE';
  const d = section.details || {};
  const estate = d.estate;
  const demo = d.demographics || {};
  const crime = d.crime;
  const burglaryDown = crime && crime.burglary_yoy_pct <= 0;

  return (
    <div className="space-y-4 pt-2">
      {/* Estate identical-floorplan card — only when we detect a builder estate */}
      {estate?.sales && (
        <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-4" data-testid="passport-estate-card">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-4 w-4 text-purple-700" />
            <span className="font-semibold text-stone-900">{estate.estate} — &lsquo;{estate.sales.plan_name}&rsquo; floorplan</span>
            <Badge variant="outline" className="text-[10px] py-0 text-purple-700 border-purple-300">
              {estate.builder}
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase text-stone-500 font-semibold">Identical units sold</div>
              <div className="text-lg font-bold text-stone-900">{estate.sales.sold_count}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-stone-500 font-semibold">Median sold price</div>
              <div className="text-lg font-bold text-purple-700">{fmtEur(estate.sales.median_price_eur)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-stone-500 font-semibold">Last sold</div>
              <div className="text-lg font-bold text-stone-900">
                {new Date(estate.sales.last_sold).toLocaleDateString(locale, { month: 'short', year: 'numeric' })}
              </div>
            </div>
          </div>
          <div className="text-[10px] text-stone-400 mt-2">Source: {isUK ? 'HM Land Registry Price Paid' : 'Land Registry + builder index'}. {isUK ? '' : 'Floor-plan match is name-based.'}</div>
        </div>
      )}

      {/* Demographics + Crime side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-demographics-card">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-purple-700" />
            <span className="font-semibold text-stone-900">Demographics</span>
            {d.demographics_is_national_fallback && (
              <Badge variant="outline" className="text-[10px] py-0 text-stone-500">National avg</Badge>
            )}
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <div className="flex justify-between text-xs"><span className="text-stone-600">Median age</span><span className="font-semibold">{demo.median_age}</span></div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-stone-600">Under 15</span><span className="font-semibold">{demo.pct_under_15}%</span></div>
              <Pct pct={demo.pct_under_15} color="bg-emerald-500" />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-stone-600">Over 65</span><span className="font-semibold">{demo.pct_over_65}%</span></div>
              <Pct pct={demo.pct_over_65} color="bg-amber-500" />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-stone-600">Family households</span><span className="font-semibold">{demo.family_share_pct}%</span></div>
              <Pct pct={demo.family_share_pct} color="bg-purple-500" />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-stone-600">Rented (vs owned)</span><span className="font-semibold">{demo.rented_share_pct}%</span></div>
              <Pct pct={demo.rented_share_pct} color="bg-stone-400" />
            </div>
          </div>
          <div className="text-[10px] text-stone-400 mt-2">Source: {isUK ? 'ONS Census 2021' : 'CSO Small Area Population Statistics'}.</div>
        </div>

        {crime && (
          <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="passport-crime-card">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="h-4 w-4 text-purple-700" />
              <span className="font-semibold text-stone-900">Garda division — {crime.division}</span>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-stone-700">Burglary YoY</span>
                <span className={`flex items-center gap-1 font-semibold ${burglaryDown ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {burglaryDown ? <TrendingDown className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />}
                  {crime.burglary_yoy_pct > 0 ? '+' : ''}{crime.burglary_yoy_pct}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-700">Theft YoY</span>
                <span className={`flex items-center gap-1 font-semibold ${crime.theft_yoy_pct <= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {crime.theft_yoy_pct <= 0 ? <TrendingDown className="h-3.5 w-3.5" /> : <TrendingUp className="h-3.5 w-3.5" />}
                  {crime.theft_yoy_pct > 0 ? '+' : ''}{crime.theft_yoy_pct}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-700">Overall level</span>
                <Badge className={
                  crime.level === 'Low' ? 'bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] py-0' :
                  crime.level === 'Moderate' ? 'bg-amber-100 text-amber-900 border-amber-200 text-[10px] py-0' :
                  'bg-red-100 text-red-800 border-red-200 text-[10px] py-0'
                }>{crime.level}</Badge>
              </div>
            </div>
            <div className="text-[10px] text-stone-400 mt-2">Source: An Garda Síochána divisional quarterlies.</div>
          </div>
        )}
      </div>

      <div className="text-[10px] text-stone-400">
        {isUK
          ? 'Neighbour signals from HM Land Registry Price Paid · ONS Census 2021. Crime statistics from police.uk are wired in a follow-up sprint.'
          : 'Neighbour signals from Land Registry · builder estate index · CSO SAPS 2022 · An Garda Síochána quarterly division releases.'}
      </div>
    </div>
  );
};

export default FutureNeighboursSection;
